import { useState, useEffect } from "react";
import { useNotification } from "./useNotification";
import { useApiErrorHandler } from "./useApiErrorHandler";
import useTokenRefresh from "./useTokenRefresh";
import httpclient from "../Utils";

export const useMasterSetting = (options = {}) => {
  const {
    autoFetch = true,
    pagination = { page: 0, pageSize: 20 },
    sorting = [],
    transformData = null,
  } = options;

  const [masterSettings, setMasterSettings] = useState([]);
  const [timeList, setTimeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchMasterSettings = async (customParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.page + 1,
        per_page: pagination.pageSize,
        ...customParams,
      };

      if (sorting.length > 0) {
        const sortField = sorting[0].field;
        const sortOrder = sorting[0].sort;
        params.sort_by = sortField;
        params.sort_order = sortOrder;
      }

      const res = await httpclient.get("/master-settings?checkError=1", { params });
      const { data } = res;

      if (data.status === 200) {
        const settings = transformData ? transformData(data.data) : data.data;
        setMasterSettings(settings);
        setRowCount(data.meta?.total || data.count || 0);
        setTimeList(data.time_list || []);
      } else {
        const errorMsg = data.message || "Failed to fetch master settings";
        setError(errorMsg);
        showNotification("error", errorMsg);
      }
    } catch (error) {
      const errorMsg = "Failed to fetch master settings";
      setError(errorMsg);

      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchMasterSettings();
    }
  }, [refreshTrigger, pagination.page, pagination.pageSize, JSON.stringify(sorting)]);

  const refreshMasterSettings = (params = {}) => {
    if (autoFetch) {
      setRefreshTrigger((prev) => prev + 1);
    } else {
      fetchMasterSettings(params);
    }
  };

  const updateMasterSetting = async (id, settingData) => {
    try {
      setLoading(true);
      const res = await httpclient.post(`/master-settings/update/${id}?checkError=1`, settingData);
      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        refreshMasterSettings();
        return { success: true, data: data.data };
      } else {
        showNotification("error", data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      setLoading(true);
      const res = await httpclient.post(`/master-settings/status/${id}`, { status });
      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);
        refreshMasterSettings();
        return { success: true, data: data.data };
      } else {
        showNotification("error", data.message);
        return { success: false, error: data.message };
      }
    } catch (error) {
      handleApiError(error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return {
    masterSettings,
    timeList,
    loading,
    error,
    rowCount,

    fetchMasterSettings,
    refreshMasterSettings,
    updateMasterSetting,
    changeStatus,
  };
};
